/**
 * PERSON MAPPERS — conversione tra tipi UI (union string) e DTO REST (int32).
 *
 * Pattern di riferimento: vedi taskMappers.ts. Stesso approccio:
 * - tipi UI in union string per leggibilita',
 * - backend REST espone int32 (allineati a `getValue()` delle enum Java
 *   in it.dvel.crm.util.constants.Person*),
 * - conversione ESCLUSIVAMENTE ai boundary (dentro personApi.ts).
 *
 * Fonte di verita' dei types Person: `Person`, `LeadStatus`,
 * `MarketingStatus`, `PersonStatus`, `PersonDto` vivono qui. personTypes
 * li re-esporta e aggiunge i tipi UI-centrici (PersonState, payload
 * create/update, costanti LEAD_STATUSES/MARKETING_STATUSES con label i18n).
 *
 * Divergenza semantica nota — PersonLeadStatus:
 *   Il backend ha 3 valori (NEW, CURRENT, CLOSE, tutti marcati
 *   "TODO Valori provvisori"). Il frontend mock aveva 4 valori
 *   (nuovo/contattato/qualificato/perso). Scelta: il frontend si
 *   adegua al backend (3 valori). Mapping: nuovo=0, in_lavorazione=1,
 *   chiuso=2. Quando il backend introdurra' piu' granularita', ampliare
 *   qui e nei mock/UI.
 */

import { enumFromDto, pickAuditFields, type AuditFields } from '@crm/shared';
import type { EmailAddress } from '../emailAddress/emailAddressTypes';
import type { Phone } from '../phone/phoneTypes';

// ─── Types UI (fonte di verita' per Person enum) ───────────────────────
//
// SYNC CROSS-LAYER — vedi docblock di ogni enum Java (crm-util):
//   LeadStatus        -> modules/crm-util/.../PersonLeadStatus.java
//                        + personTypes.ts (LEAD_STATUSES)
//                        + locales/{it,en}.json (person.leadStatuses.*)
//   MarketingStatus   -> modules/crm-util/.../PersonMarketingStatus.java
//                        + personTypes.ts (MARKETING_STATUSES)
//                        + locales/{it,en}.json (person.marketingStatuses.*)
//   PersonStatus      -> modules/crm-util/.../PersonStatus.java
//                        (server-driven, solo lettura)

export type LeadStatus = 'nuovo' | 'in_lavorazione' | 'chiuso';

export type MarketingStatus =
  | 'lead'
  | 'marketing_qualified'
  | 'sales_qualified'
  | 'customer';

export type PersonStatus = 'inattivo' | 'attivo';

// ─── Tabelle di mapping ────────────────────────────────────────────────
// SYNC with PersonLeadStatus.java: NEW=0, CURRENT=1, CLOSE=2
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/PersonLeadStatus.java
const LEAD_STATUS_TO_INT: Record<LeadStatus, number> = {
  nuovo: 0,
  in_lavorazione: 1,
  chiuso: 2,
};
const LEAD_STATUS_FROM_INT: Record<number, LeadStatus> = invert(LEAD_STATUS_TO_INT);

// SYNC with PersonMarketingStatus.java:
//   LEAD=0, CUSTOMER=1, MARKETING_QUALIFIED=2, SALES_QUALIFIED=3
// NB: ordine "stranamente" non progressivo (customer viene prima dei
// qualified), rispecchiato qui 1:1 col backend.
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/PersonMarketingStatus.java
const MARKETING_STATUS_TO_INT: Record<MarketingStatus, number> = {
  lead: 0,
  customer: 1,
  marketing_qualified: 2,
  sales_qualified: 3,
};
const MARKETING_STATUS_FROM_INT: Record<number, MarketingStatus> = invert(MARKETING_STATUS_TO_INT);

// SYNC with PersonStatus.java: INACTIVE=0, ACTIVE=1
// Server-driven in PATCH (il backend non accetta lo status in updatePerson);
// letto dal client come read-only.
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/PersonStatus.java
const PERSON_STATUS_TO_INT: Record<PersonStatus, number> = {
  inattivo: 0,
  attivo: 1,
};
const PERSON_STATUS_FROM_INT: Record<number, PersonStatus> = invert(PERSON_STATUS_TO_INT);

// ─── API di mapping singolo enum ───────────────────────────────────────
// File-local: le funzioni di boundary (personFromDto, personToDto, ecc.)
// le invocano internamente. Il fallback soft + warn su int fuori-range e'
// centralizzato in `enumFromDto` (@crm/shared/utils/enumFromDto).

const leadStatusToDto = (v: LeadStatus): number => LEAD_STATUS_TO_INT[v];
const leadStatusFromDto = (v: number): LeadStatus =>
  enumFromDto('PersonLeadStatus', v, LEAD_STATUS_FROM_INT, 'nuovo');

const marketingStatusToDto = (v: MarketingStatus): number => MARKETING_STATUS_TO_INT[v];
const marketingStatusFromDto = (v: number): MarketingStatus =>
  enumFromDto('PersonMarketingStatus', v, MARKETING_STATUS_FROM_INT, 'lead');

const personStatusToDto = (v: PersonStatus): number => PERSON_STATUS_TO_INT[v];
const personStatusFromDto = (v: number): PersonStatus =>
  enumFromDto('PersonStatus', v, PERSON_STATUS_FROM_INT, 'attivo');

// ─── DTO completo del Person (shape REST, con int al posto degli enum) ──

/**
 * Shape dei sub-object nested (EmailAddress, Phone) come escono dal REST
 * dentro un Person. Schema Liferay-standard: `it.dvel.crm.core` li
 * modella riusando l'entita' base del portal (EmailAddress, Phone del
 * core). I type TS locali del frontend vivono altrove
 * (entities/emailAddress, entities/phone) e saranno riallineati in 1d.
 */
export interface EmailAddressDto {
  emailAddressId: number;
  address: string;
  primary: boolean;
}

export interface PhoneDto {
  phoneId: number;
  number: string;
  primary: boolean;
}

/**
 * Shape del Person come esce dal REST (/o/crm/v1_0/person/{personId} e
 * nested in /people/{groupId}). Rispecchia schema Person del rest-openapi.yaml.
 * Gli enum sono int32; status e' server-driven (presente in lettura,
 * non accettato in update). Usato solo da personApi/personMappers.
 */
export interface PersonDto {
  personId: number;
  externalCode: string | null;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  firstName: string;
  lastName: string;
  location: string | null;
  /**
   * FK verso PersonSource. 0 = nessuna source associata.
   * In lettura il backend denormalizza anche `personSource` (nome) per
   * evitare round-trip.
   */
  personSourceId: number;
  /**
   * Nome della PersonSource associata (denormalizzato in lettura).
   * Vuoto se personSourceId=0 o se la source è stata cancellata
   * (cascade zero-out lato backend).
   */
  personSource: string;
  /** FK verso PersonChannel (clone esatto della logica personSource). */
  personChannelId: number;
  /** Nome del PersonChannel associato (denormalizzato in lettura). */
  personChannel: string;
  lastInteractionDate: string;
  lastCampaignDate: string | null;
  leadStatus: number;
  marketingStatus: number;
  status: number;
  emailAddresses: EmailAddressDto[];
  phones: PhoneDto[];
  /**
   * Stato della pipeline in cui il contatto è collocato: la colonna della bacheca. 0 = non
   * collocato. Non è una colonna di crm_Person — è una riga di PipelineMembership che il backend
   * risolve prima di rispondere. Opzionale perché le risposte precedenti a TS-117 non lo portano.
   */
  currentStateId?: number;
}

// ─── Shape UI del Person (con enum come union string) ─────────────────

export interface Person extends AuditFields {
  personId: number;
  /**
   * Codice identificativo esterno. Predisposto end-to-end (REST + mock)
   * ma NON esposto nell'UI come scelta di prodotto: il frontend non lo mostra
   * né lo edita per Person, resta `null` per i record creati via Form Person.
   * Per Business invece è visibile come prima colonna/InfoLine.
   */
  externalCode: string | null;
  firstName: string;
  lastName: string;
  location: string | null;
  /** FK verso PersonSource. 0 = nessuna source. */
  personSourceId: number;
  /** Nome PersonSource (denormalizzato dal backend). Vuoto se id=0. */
  personSource: string;
  /** FK verso PersonChannel. 0 = nessun channel. */
  personChannelId: number;
  /** Nome PersonChannel (denormalizzato dal backend). Vuoto se id=0. */
  personChannel: string;
  lastInteractionDate: string;
  lastCampaignDate: string | null;
  leadStatus: LeadStatus;
  marketingStatus: MarketingStatus;
  status: PersonStatus;
  emailAddresses: EmailAddress[];
  phones: Phone[];
  /** Colonna della bacheca in cui sta il contatto. 0 = non collocato. Vedi PersonDto. */
  currentStateId: number;
}

// ─── DTO di input (POST /people/{groupId} e PATCH /person/{id}) ────────

/**
 * Shape del payload che il backend accetta in create/update Person.
 * Allineato a PersonInput in rest-openapi.yaml: include solo i campi
 * modificabili via REST; esclude audit, status (server-driven),
 * lastInteractionDate/lastCampaignDate (server-driven), personId (path),
 * emailAddresses/phones (CRUD via endpoint dedicati nested).
 * Le enum viaggiano come int32.
 */
export interface PersonInput {
  /** Codice identificativo esterno. Predisposto ma non popolato dall'UI core. */
  externalCode?: string | null;
  firstName?: string;
  lastName?: string;
  location?: string | null;
  /**
   * FK verso PersonSource esistente nel gruppo. 0 = nessuna source.
   * Alternativo a `personSource`. Se entrambi valorizzati, prevale `personSourceId`
   * (vedi PeopleResourceImpl backend).
   */
  personSourceId?: number;
  /**
   * Nome libero della source. Se valorizzato senza `personSourceId`,
   * il backend fa find-or-create case-insensitive (finder G_N) e
   * associa l'id alla Person.
   */
  personSource?: string;
  /** FK verso PersonChannel (clone semantica personSourceId). */
  personChannelId?: number;
  /** Nome libero del channel (clone semantica personSource). */
  personChannel?: string;
  leadStatus?: number;
  marketingStatus?: number;
  /**
   * Colonna della bacheca in cui spostare il contatto. 0 lo toglie dalla board. Viaggia in questo
   * payload ma lato server passa dal canale dedicato `updateCurrentState`: non è il leadStatus e
   * non lo tocca.
   */
  currentStateId?: number;
  /**
   * Email e telefono primari, letti dal backend solo in creazione e salvati nella stessa
   * transazione della persona: se uno dei due è rifiutato, la persona non viene creata.
   */
  emailAddress?: string;
  phoneNumber?: string;
}

// ─── Whole-DTO mappers ─────────────────────────────────────────────────

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare esattamente
 * la shape che il backend emette (enum come int32). In produzione, chi
 * invia un Person al backend passa per `personCreateToInput` /
 * `personUpdateToInput` — questo whole-DTO converter non viene chiamato
 * dall'app reale, solo dal mock storage.
 */
export function personToDto(p: Person): PersonDto {
  return {
    personId: p.personId,
    externalCode: p.externalCode,
    ...pickAuditFields(p),
    firstName: p.firstName,
    lastName: p.lastName,
    location: p.location,
    personSourceId: p.personSourceId,
    personSource: p.personSource,
    personChannelId: p.personChannelId,
    personChannel: p.personChannel,
    lastInteractionDate: p.lastInteractionDate,
    lastCampaignDate: p.lastCampaignDate,
    leadStatus: leadStatusToDto(p.leadStatus),
    marketingStatus: marketingStatusToDto(p.marketingStatus),
    status: personStatusToDto(p.status),
    emailAddresses: p.emailAddresses,
    phones: p.phones,
    currentStateId: p.currentStateId,
  };
}

/**
 * DTO input (PATCH body) -> Partial<Person> UI. Usato dai mock MSW per
 * applicare il patch al mockPersons storage (che tiene la shape UI).
 */
export function personInputFromDto(input: PersonInput): Partial<Person> {
  const out: Partial<Person> = {};
  if (input.externalCode !== undefined)
    out.externalCode = input.externalCode;
  if (input.firstName !== undefined) out.firstName = input.firstName;
  if (input.lastName !== undefined) out.lastName = input.lastName;
  if (input.location !== undefined) out.location = input.location;
  // Dual-field passthrough: il mapper non risolve la find-or-create (lo fa
  // il backend, oppure il mock handler post-processando questo patch).
  if (input.personSourceId !== undefined) out.personSourceId = input.personSourceId;
  if (input.personSource !== undefined) out.personSource = input.personSource;
  if (input.personChannelId !== undefined) out.personChannelId = input.personChannelId;
  if (input.personChannel !== undefined) out.personChannel = input.personChannel;
  if (input.leadStatus !== undefined) out.leadStatus = leadStatusFromDto(input.leadStatus);
  if (input.marketingStatus !== undefined) out.marketingStatus = marketingStatusFromDto(input.marketingStatus);
  if (input.currentStateId !== undefined) out.currentStateId = input.currentStateId;
  return out;
}

/** DTO REST -> modello UI. Usato su GET / response POST / response PATCH. */
export function personFromDto(dto: PersonDto): Person {
  return {
    personId: dto.personId,
    externalCode: dto.externalCode ?? null,
    ...pickAuditFields(dto),
    firstName: dto.firstName,
    lastName: dto.lastName,
    location: dto.location,
    // Person vecchie (pre-introduzione dei channel/source) possono avere
    // questi campi NULL nel DB. Il type UI dichiara `number`/`string`, e
    // MUI Autocomplete fa boom su null (legge inputValue.length). Normalizzo
    // qui al boundary DTO→UI così l'intera app lavora con valori safe.
    personSourceId: dto.personSourceId ?? 0,
    personSource: dto.personSource ?? '',
    personChannelId: dto.personChannelId ?? 0,
    personChannel: dto.personChannel ?? '',
    lastInteractionDate: dto.lastInteractionDate,
    lastCampaignDate: dto.lastCampaignDate,
    leadStatus: leadStatusFromDto(dto.leadStatus),
    marketingStatus: marketingStatusFromDto(dto.marketingStatus),
    status: personStatusFromDto(dto.status),
    emailAddresses: dto.emailAddresses as EmailAddress[],
    phones: dto.phones as Phone[],
    // `?? 0` e non un default più in alto: 0 è il "non collocato" del contratto, e una risposta che
    // il campo non lo porta affatto significa la stessa cosa.
    currentStateId: dto.currentStateId ?? 0,
  };
}

/**
 * Applica la logica dual-field source a un PersonInput parziale (muta in
 * place). Decide quale campo serializzare:
 *   - se `personSourceId > 0` -> invia solo `personSourceId` (id prevale,
 *     il backend ignora `personSource` se sono entrambi presenti)
 *   - altrimenti se `personSource` non vuoto -> invia solo `personSource`
 *     (find-or-create case-insensitive lato backend)
 *   - altrimenti se `allowReset` e l'utente ha esplicitato (id=0 && name='')
 *     -> invia `personSourceId: 0` (reset semantico per PATCH).
 *   - in tutti gli altri casi non scrive nulla (nessuna source associata).
 *
 * `allowReset=false` per CREATE (form invia solo se valorizzato);
 * `allowReset=true` per UPDATE (PATCH puo' richiedere reset esplicito).
 */
function writeDualFieldSource(
  out: PersonInput,
  personSourceId: number | undefined,
  personSource: string | undefined,
  allowReset: boolean,
): void {
  if (personSourceId !== undefined && personSourceId > 0) {
    out.personSourceId = personSourceId;
  } else if (personSource !== undefined && personSource.trim() !== '') {
    out.personSource = personSource;
  } else if (allowReset && personSourceId === 0 && personSource === '') {
    out.personSourceId = 0;
  }
}

/** Clone esatto di writeDualFieldSource per il campo personChannel. */
function writeDualFieldChannel(
  out: PersonInput,
  personChannelId: number | undefined,
  personChannel: string | undefined,
  allowReset: boolean,
): void {
  if (personChannelId !== undefined && personChannelId > 0) {
    out.personChannelId = personChannelId;
  } else if (personChannel !== undefined && personChannel.trim() !== '') {
    out.personChannel = personChannel;
  } else if (allowReset && personChannelId === 0 && personChannel === '') {
    out.personChannelId = 0;
  }
}

/** Payload create UI -> DTO input. Tutti i campi richiesti dal form UI. */
export function personCreateToInput(p: {
  externalCode?: string | null;
  firstName: string;
  lastName: string;
  location?: string | null;
  personSourceId: number;
  personSource: string;
  personChannelId: number;
  personChannel: string;
  leadStatus: LeadStatus;
  marketingStatus: MarketingStatus;
  email?: string;
  phone?: string;
}): PersonInput {
  const out: PersonInput = {
    externalCode: p.externalCode ?? null,
    firstName: p.firstName,
    lastName: p.lastName,
    location: p.location ?? null,
    leadStatus: leadStatusToDto(p.leadStatus),
    marketingStatus: marketingStatusToDto(p.marketingStatus),
  };
  writeDualFieldSource(out, p.personSourceId, p.personSource, false);
  writeDualFieldChannel(out, p.personChannelId, p.personChannel, false);
  // Campi vuoti omessi: il backend non aggiunge niente, ma una stringa di soli spazi
  // arriverebbe alla validazione dell'email e farebbe fallire la creazione.
  const email = p.email?.trim();
  const phone = p.phone?.trim();
  if (email) out.emailAddress = email;
  if (phone) out.phoneNumber = phone;
  return out;
}

/**
 * Payload update UI -> DTO input parziale (PATCH). Include solo i campi
 * effettivamente presenti nel payload: il backend ignora gli altri.
 * Attenzione: `location: null` e' semanticamente diverso da `location`
 * assente → qui rispettiamo la distinzione (null viene inviato).
 */
export function personUpdateToInput(p: {
  externalCode?: string | null;
  firstName?: string;
  lastName?: string;
  location?: string | null;
  personSourceId?: number;
  personSource?: string;
  personChannelId?: number;
  personChannel?: string;
  leadStatus?: LeadStatus;
  marketingStatus?: MarketingStatus;
  currentStateId?: number;
}): PersonInput {
  const out: PersonInput = {};
  if (p.externalCode !== undefined) out.externalCode = p.externalCode;
  if (p.firstName !== undefined) out.firstName = p.firstName;
  if (p.lastName !== undefined) out.lastName = p.lastName;
  if (p.location !== undefined) out.location = p.location;
  writeDualFieldSource(out, p.personSourceId, p.personSource, true);
  writeDualFieldChannel(out, p.personChannelId, p.personChannel, true);
  if (p.leadStatus !== undefined) out.leadStatus = leadStatusToDto(p.leadStatus);
  if (p.marketingStatus !== undefined) out.marketingStatus = marketingStatusToDto(p.marketingStatus);
  if (p.currentStateId !== undefined) out.currentStateId = p.currentStateId;
  return out;
}

// ─── Helper ────────────────────────────────────────────────────────────

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
